import type {
  ItineraryEditor,
  ItineraryCustomerOutput,
  NodeType,
  PublicItinerary,
  PublicItinerarySegment,
  PublicTimeSlot,
  PublicTransportOption,
} from "../types";
import { isHttpClientError } from "../../../lib/http/errors";
import { isPendingTransportSource, rebuildAdjacentSegments } from "./itineraryEditorService";
import { buildInheritedDayNarratives, hasTextContent } from "./itineraryNarrativeService";
export { composePointNarrative } from "./itineraryNarrativeService";

const publicTimeSlotOrder: PublicTimeSlot[] = ["morning", "afternoon", "evening", "unscheduled"];

/** 去掉服务正文开头的序号，保留数字事实、正文空白与后续段落。 */
export function stripServiceStandardLeadingNumber(content: string): string {
  return content.replace(/^\s*\d+(?:、[ \t]*|[.．][ \t]+)/, "");
}

/** 网页预览去掉条目开头的旧序号，保留数字事实、正文格式与保存原文。 */
export function normalizeServiceStandardPreviewRecords(records: readonly string[]): string[] {
  return records
    .map((item) => stripServiceStandardLeadingNumber(item.trim()).trim())
    .filter(Boolean);
}

/** 客户输出只按日级兼容开关判断；来源值不再决定追加。 */
export function isPublicSummaryComposed(
  day: PublicItinerary["days"][number],
  slot: PublicTimeSlot,
): boolean {
  void slot;
  return day.legacyCompatibility === false;
}

/** 全天归为一个正文区域；未初始化时保留分时原文，历史总述回退仍不叠加标题。 */
export function buildDayNarratives(
  day: PublicItinerary["days"][number],
  nodes = day.nodes,
): { slot: PublicTimeSlot | "full_day"; paragraphs: { content: string; pointTitle?: string }[] }[] {
  if (day.isFullDay && day.fullDaySummary != null) {
    if (!hasTextContent(day.fullDaySummary)) return [];
    return [{ slot: "full_day", paragraphs: [{ content: day.fullDaySummary.replace(/<br\s*\/?\s*>/gi, "\n").trim() }] }];
  }
  const parts = buildInheritedDayNarratives(day, nodes);
  return day.isFullDay && parts.length && parts[0].slot !== "unscheduled"
    ? [{ slot: "full_day", paragraphs: parts.flatMap((part) => part.paragraphs) }]
    : parts;
}
const publicNodeTypeLabels: Record<NodeType, string> = {
  point: "考察点",
  railway_station: "火车站",
  airport: "机场",
  stay_area: "住宿区域",
  organization_site: "学校",
  custom: "自定义点",
};

/** 住宿端点固定标注区域，同名异类的其他端点补充类型以区分实际路线。 */
export function publicRouteEndpointTitles(
  fromNode: ItineraryEditor["days"][number]["nodes"][number] | PublicItinerary["days"][number]["nodes"][number],
  toNode: ItineraryEditor["days"][number]["nodes"][number] | PublicItinerary["days"][number]["nodes"][number],
): [string, string] {
  const sameNameDifferentTypes = fromNode.title.trim() === toNode.title.trim()
    && fromNode.nodeType !== toNode.nodeType;
  const title = (node: typeof fromNode) => {
    if (node.nodeType === "stay_area") return node.title.trim().endsWith("（住宿区域）")
      ? node.title.trim() : `${node.title.trim()}（住宿区域）`;
    return sameNameDifferentTypes ? `${node.title}（${publicNodeTypeLabels[node.nodeType]}）` : node.title;
  };
  return [title(fromNode), title(toNode)];
}

/** 只识别有稳定主数据引用、且没有任何交通事实的跨日连续性结构线。 */
export function isStructuralDayContinuity(
  segment: ItineraryEditor["segments"][number],
  nodesById: Map<string, ItineraryEditor["days"][number]["nodes"][number]>,
): boolean {
  if (
    segment.startDayId === segment.endDayId
    || segment.routeType !== "local_transfer"
    || segment.transportMode !== "walk"
    || !(isPendingTransportSource(segment.sourceType) || segment.sourceType === "default_walk")
  ) return false;
  const fromNode = nodesById.get(segment.fromNodeId);
  const toNode = nodesById.get(segment.toNodeId);
  if (!fromNode || !toNode) return false;
  const sameResource = [
    [fromNode.pointSpuId, toNode.pointSpuId],
    [fromNode.stayAreaId, toNode.stayAreaId],
    [fromNode.organizationSiteId, toNode.organizationSiteId],
  ].some(([left, right]) => left !== null && left !== undefined && left === right);
  const hasTransportFacts = Boolean(
    segment.routeCode
    || segment.departureAt
    || segment.arrivalAt
    || segment.distanceKm
    || segment.durationMinutes !== null && segment.durationMinutes !== undefined
    || segment.segmentDescription
    || segment.sourcePayload?.selected
    || segment.sourcePayload?.backups.length,
  );
  return sameResource && !hasTransportFacts;
}

type OutputOperation = "share" | "pdf";

/** 将输出与分享失败收敛为稳定、可行动且不泄露底层实现的用户提示。 */
export function outputOperationErrorMessage(
  operation: OutputOperation,
  error: unknown,
  options: { shareLinkCreated?: boolean } = {},
): string {
  if (operation === "pdf") return "PDF 下载失败，请稍后重试。";
  if (options.shareLinkCreated) {
    return "分享链接已生成，但复制失败，请允许剪贴板权限后重试。";
  }
  if (!isHttpClientError(error)) return "分享失败，请稍后重试。";
  if (error.statusCode === null || error.code === "ERR_NETWORK") {
    return "分享失败，请检查网络后重试。";
  }
  if (error.statusCode === 403) return "当前账号没有分享权限，请联系管理员。";
  if (error.statusCode === 404) return "行程不存在或已删除，无法生成分享链接。";
  if (error.statusCode === 409) return "行程状态已变化，请刷新页面后重试。";
  if (error.statusCode === 400 || error.statusCode === 422) {
    return "分享请求无效，请刷新页面后重试。";
  }
  if (error.retriable || error.statusCode >= 500) {
    return "分享服务暂时不可用，请稍后重试。";
  }
  return "分享失败，请稍后重试。";
}

/** 仅当整份历史快照没有任何交通事实时，按相邻点位补出普通步行展示线路。 */
export function inferPendingPublicRoutes(itinerary: PublicItinerary): PublicItinerary {
  const existingSegmentCount = itinerary.days.reduce((count, day) => count
    + day.segments.length
    + (day.parallelTracks ?? []).reduce((trackCount, track) => trackCount + track.segments.length, 0), 0);
  if (existingSegmentCount > 0) return itinerary;

  const defaultWalkSegments = (
    nodes: PublicItinerary["days"][number]["nodes"],
  ): PublicItinerarySegment[] => {
    const ordered = [...nodes].sort((left, right) =>
      publicTimeSlotOrder.indexOf(left.timeSlot) - publicTimeSlotOrder.indexOf(right.timeSlot)
      || left.sortOrder - right.sortOrder,
    );
    return ordered.slice(0, -1).map((fromNode, index) => {
      const toNode = ordered[index + 1];
      const [fromTitle, toTitle] = publicRouteEndpointTitles(fromNode, toNode);
      return {
        name: `${fromTitle} - ${toTitle}`,
        fromTitle,
        toTitle,
        timeSlot: toNode.timeSlot,
        transportMode: "walk" as const,
        isPending: false,
        hasStayEndpoint: fromNode.nodeType === "stay_area" || toNode.nodeType === "stay_area",
        backupTransports: [],
      };
    });
  };

  const next = structuredClone(itinerary);
  next.days = next.days.map((day) => ({
    ...day,
    segments: day.parallelTracks?.length ? [] : defaultWalkSegments(day.nodes),
    parallelTracks: day.parallelTracks?.map((track) => ({
      ...track,
      segments: defaultWalkSegments(track.nodes),
    })),
  }));
  return next;
}

/** 从编辑器快照派生客户只读行程，不暴露内部标识和来源字段。 */
export function derivePublicItinerary(
  editor: ItineraryEditor,
  customerOutput?: ItineraryCustomerOutput,
): PublicItinerary {
  let inferredSegmentIndex = 0;
  const sourceSegments = editor.segments.length
    ? editor.segments
    : rebuildAdjacentSegments(
        editor.days,
        editor.segments,
        () => `inferred-pending-${inferredSegmentIndex += 1}`,
      );
  const nodesById = new Map(editor.days.flatMap((day) => day.nodes).map((node) => [node.id, node]));
  const customerSegments = sourceSegments.filter(
    (segment) => !isStructuralDayContinuity(segment, nodesById),
  );
  const segmentsById = new Map(customerSegments.map((segment) => [segment.id, segment]));
  const publicNode = (node: ItineraryEditor["days"][number]["nodes"][number]) => ({
    nodeType: node.nodeType,
    timeSlot: node.timeSlot,
    sortOrder: node.sortOrder,
    title: node.title,
    description: pointDescription(node),
    imageUrl: node.imageUrl ?? null,
    categoryLabel: node.categoryLabel ?? null,
    planStartAt: node.planStartAt,
    planEndAt: node.planEndAt,
  });
  const publicTransport = (selection: NonNullable<ItineraryEditor["segments"][number]["sourcePayload"]>["selected"]): PublicTransportOption | null => selection ? {
    label: selection.label,
    routeCode: selection.routeCode,
    seatClass: selection.seatClass,
    cabinClass: selection.cabinClass,
    departureAt: selection.departureAt,
    arrivalAt: selection.arrivalAt,
    distanceKm: selection.distanceKm,
    durationMinutes: selection.durationMinutes,
  } : null;
  const publicSegment = (segment: ItineraryEditor["segments"][number]) => {
    const fromNode = nodesById.get(segment.fromNodeId);
    const toNode = nodesById.get(segment.toNodeId);
    const [fromTitle, toTitle] = fromNode && toNode
      ? publicRouteEndpointTitles(fromNode, toNode)
      : [fromNode?.title ?? "出发点", toNode?.title ?? "目的地"];
    return {
    name: `${fromTitle} - ${toTitle}`,
    fromTitle,
    toTitle,
    timeSlot: fromNode?.timeSlot ?? "morning" as const,
    transportMode: segment.transportMode,
    isPending: isPendingTransportSource(segment.sourceType),
    isInterDay: segment.startDayId !== segment.endDayId,
    hasStayEndpoint: fromNode?.nodeType === "stay_area" || toNode?.nodeType === "stay_area",
    routeCode: segment.sourcePayload?.selected?.routeCode ?? segment.routeCode,
    departureAt: segment.sourcePayload?.selected?.departureAt ?? segment.departureAt,
    arrivalAt: segment.sourcePayload?.selected?.arrivalAt ?? segment.arrivalAt,
    distanceKm: segment.sourcePayload?.selected?.distanceKm ?? segment.distanceKm,
    durationMinutes: segment.sourcePayload?.selected?.durationMinutes ?? segment.durationMinutes,
    description: segment.transportMode === "walk" ? null : segment.segmentDescription,
    primaryTransport: publicTransport(segment.sourcePayload?.selected),
    backupTransports: (segment.sourcePayload?.backups ?? []).map((item) => publicTransport(item)).filter((item): item is PublicTransportOption => item !== null),
    };
  };
  return inferPendingPublicRoutes({
    title: editor.title,
    startDate: editor.startDate,
    endDate: editor.endDate,
    summary: editor.summary,
    customerServiceStandards: editor.customerServiceStandards,
    customerNotices: editor.customerNotices,
    pdfHeadImageUrls: [],
    groupNumber: customerOutput?.groupNumber,
    hotels: customerOutput?.hotels ?? [],
    days: editor.days.map((day) => {
      const trackSegmentIds = new Set(day.parallelTracks?.tracks.flatMap((track) => track.segmentIds) ?? []);
      return {
      dayNumber: day.dayNumber,
      dayTitle: day.dayTitle,
      dayDate: day.dayDate,
      stayAreaName: day.stayAreaName,
      legacyCompatibility: day.legacyCompatibility ?? false,
      morningSummary: day.morningSummary,
      morningSummaryComposed: !day.legacyCompatibility,
      afternoonSummary: day.afternoonSummary,
      afternoonSummaryComposed: !day.legacyCompatibility,
      eveningSummary: day.eveningSummary,
      eveningSummaryComposed: !day.legacyCompatibility,
      summary: day.summary,
      fullDaySummary: day.fullDaySummary,
      isFullDay: day.isFullDay,
      nodes: day.nodes.map(publicNode),
      segments: customerSegments
        .filter((segment) => segment.startDayId === day.id && !trackSegmentIds.has(segment.id))
        .map(publicSegment),
      parallelTracks: day.parallelTracks?.tracks.map((track) => ({
        title: track.title,
        nodes: track.nodeIds.map((id) => nodesById.get(id)).filter((node) => node !== undefined).map(publicNode),
        segments: track.segmentIds.map((id) => segmentsById.get(id)).filter((segment) => segment !== undefined).map(publicSegment),
      })) ?? [],
      };
    }),
  });
}

/** 统计定稿后真正会进入客户输出的待确认交通，不包含纯结构衔接线。 */
export function countCustomerVisiblePendingTransports(editor: ItineraryEditor): number {
  return derivePublicItinerary(editor).days.reduce((count, day) => count
    + day.segments.filter((segment) => segment.isPending).length
    + (day.parallelTracks ?? []).reduce(
      (trackCount, track) => trackCount + track.segments.filter((segment) => segment.isPending).length,
      0,
    ), 0);
}

/** 收集客户行程中的数据库考察点名称，去重后按名称长度降序返回。 */
export function databasePointNames(itinerary: PublicItinerary): string[] {
  const nodes = itinerary.days.flatMap((day) => [
    ...day.nodes,
    ...(day.parallelTracks ?? []).flatMap((track) => track.nodes),
  ]);
  return [...new Set(nodes
    .filter((node) => node.nodeType === "point")
    .map((node) => node.title.trim())
    .filter(Boolean))]
    .sort((left, right) => Array.from(right).length - Array.from(left).length);
}

/** 点位介绍仅服务真实考察点；其他节点保留在路径中但不向客户输出介绍。 */
function pointDescription(node: { nodeType: string; description?: string | null }): string | null {
  return node.nodeType === "point" ? node.description ?? null : null;
}
